// ─────────────────────────────────────────
//  UTILITIES
// ─────────────────────────────────────────

import { S, sb } from './state.js';

// ── HTML escaping ──
export function esc(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// ── Code extraction from AI responses ──
export function extractCode(text) {
  // Flexible patterns — handle \n, \r\n, space after language tag, case-insensitive
  const pats = [
    /```html[\s\n\r]+([\s\S]*?)```/i,
    /```jsx[\s\n\r]+([\s\S]*?)```/i,
    /```javascript[\s\n\r]+([\s\S]*?)```/i,
    /```css[\s\n\r]+([\s\S]*?)```/i,
    /```[\s\n\r]+([\s\S]*?)```/,
  ];
  for (const p of pats) {
    const m = text.match(p);
    if (m && m[1].trim().length > 20) return m[1].trim();
  }

  // Fallback: unclosed code fence (response was cut off) — grab everything after ```html
  const unclosed = text.match(/```(?:html|jsx|javascript)?[\s\n\r]+([\s\S]{50,})/i);
  if (unclosed && unclosed[1].includes('<')) return unclosed[1].trim();

  // Fallback: if the text contains what looks like a full HTML document, extract it
  const htmlMatch = text.match(/(<!DOCTYPE[\s\S]*<\/html>)/i);
  if (htmlMatch && htmlMatch[1].length > 50) return htmlMatch[1].trim();

  // Fallback: if text contains <html...>...</html>
  const htmlMatch2 = text.match(/(<html[\s\S]*<\/html>)/i);
  if (htmlMatch2 && htmlMatch2[1].length > 50) return htmlMatch2[1].trim();

  // Last resort: if most of the text looks like HTML/code
  const codeLines = text.split('\n').filter(l => l.match(/^\s*[<{}\[\]\/]/));
  if (codeLines.length > 10 && codeLines.length > text.split('\n').length * 0.5) {
    // More than half the lines look like code
    const startIdx = text.indexOf(codeLines[0]);
    if (startIdx >= 0) return text.slice(startIdx).trim();
  }

  return null;
}

// ── Sleep / delay ──
export function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

// ── Parse plan steps from AI response ──
export function parsePlanSteps(text) {
  // Extract numbered steps: "1. Do X" or "- Do X"
  const lines = text.split('\n');
  const steps = [];
  for (const line of lines) {
    const m = line.match(/^\s*(?:\d+[\.\)]\s*|[-•]\s+)(.+)/);
    if (m && m[1].trim().length > 5) {
      steps.push(m[1].trim());
    }
  }
  return steps.slice(0, 8); // Cap at 8 steps
}

// ── Detect app category from user prompt ──
export function detectCategory(text) {
  const t = (text || '').toLowerCase();
  const cats = [
    { key: 'invoice', patterns: /invoice|billing|receipt|quotation/ },
    { key: 'booking', patterns: /book|appointment|salon|schedule|reservation/ },
    { key: 'inventory', patterns: /inventory|stock|product|warehouse|spaza/ },
    { key: 'quiz', patterns: /quiz|trivia|question|test|exam/ },
    { key: 'budget', patterns: /budget|expense|income|finance|money/ },
    { key: 'calculator', patterns: /calculator|calculate|converter|convert/ },
    { key: 'tracker', patterns: /track|habit|goal|progress|fitness/ },
    { key: 'timer', patterns: /timer|pomodoro|countdown|stopwatch/ },
    { key: 'recipe', patterns: /recipe|cook|food|meal|kitchen/ },
    { key: 'portfolio', patterns: /portfolio|resume|cv|profile|about me/ },
    { key: 'dashboard', patterns: /dashboard|admin|analytics|report/ },
    { key: 'form', patterns: /form|survey|feedback|contact|registration/ },
    { key: 'ecommerce', patterns: /shop|store|cart|product|catalog|price list/ },
    { key: 'timetable', patterns: /timetable|schedule|planner|calendar/ },
    { key: 'password', patterns: /password|generator|security/ },
  ];
  for (const c of cats) {
    if (c.patterns.test(t)) return c.key;
  }
  return 'general';
}

// ── Toast notification ──
export function showToast(msg) {
  const t = document.getElementById('toast');
  if (!t) return;
  t.textContent = msg;
  t.classList.add('show');
  clearTimeout(t._timer);
  t._timer = setTimeout(() => t.classList.remove('show'), 2800);
}

// ── Simple string hash ──
export function simpleHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + c;
    hash |= 0;
  }
  return hash.toString(36);
}

// ── Track user signal (deploy, save, etc.) ──
export function trackSignal(signal) {
  if (!S.session) return;
  const firstMsg = S.history.find(m => m.role === 'user');
  const category = detectCategory(firstMsg?.content || '');

  sb.from('build_signals').insert({
    user_id: S.session.user.id,
    signal,
    category,
    code_hash: S.code ? simpleHash(S.code) : null,
  }).then(() => {
    // If this is a deploy or save with good code, consider it for best builds
    if ((signal === 'deploy' || signal === 'save') && S.code && S.code.length > 500) {
      submitBestBuild(category);
    }
  }).catch(() => {});
}

// ── Submit best build for gallery ──
function submitBestBuild(category) {
  if (!S.code || S.code.length < 500) return;

  const codeLines = S.code.split('\n').length;
  const hasLocalStorage = S.code.includes('localStorage');
  const hasResponsive = S.code.includes('viewport') || S.code.includes('media');

  // Calculate quality score
  let score = 0;
  score += Math.min(codeLines, 300);
  if (hasLocalStorage) score += 50;
  if (hasResponsive) score += 30;
  if (S.code.includes('addEventListener')) score += 20;
  if (!S.code.includes('alert(')) score += 20;

  // Check if we already have a build in this category with lower score
  sb.from('best_builds')
    .select('id, quality_score')
    .eq('category', category)
    .order('quality_score', { ascending: false })
    .limit(1)
    .single()
    .then(({ data: existing }) => {
      if (!existing || score > existing.quality_score) {
        const firstMsg = S.history.find(m => m.role === 'user');
        sb.from('best_builds').upsert({
          id: existing?.id || undefined,
          category,
          name: firstMsg?.content?.slice(0, 60) || category,
          code: S.code,
          quality_score: score,
          code_lines: codeLines,
          has_localstorage: hasLocalStorage,
          has_responsive: hasResponsive,
        }, { onConflict: existing ? 'id' : undefined })
          .then(() => {})
          .catch(() => {});
      }
    })
    .catch(() => {});
}

// ── Auto-resize textarea ──
export function autoResize(el) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 130) + 'px';
}

// ── Voice Input ──
let _recognition = null;
let _isRecording = false;

export function toggleVoice() {
  if (_isRecording) {
    stopVoice();
  } else {
    startVoice();
  }
}

function startVoice() {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecognition) {
    showToast('⚠ Voice input not supported in this browser');
    return;
  }

  _recognition = new SpeechRecognition();
  _recognition.lang = 'en-US';
  _recognition.interimResults = true;
  _recognition.continuous = true;
  _recognition.maxAlternatives = 1;

  const input = document.getElementById('chat-input');
  const micBtn = document.getElementById('mic-btn');
  if (!input || !micBtn) return;
  const textBefore = input.value;

  _recognition.onstart = () => {
    _isRecording = true;
    micBtn.classList.add('recording');
    micBtn.textContent = '⏹';
    input.placeholder = 'Listening... speak now';
    input.focus();
  };

  _recognition.onresult = (event) => {
    let transcript = '';
    for (let i = 0; i < event.results.length; i++) {
      transcript += event.results[i][0].transcript;
    }
    input.value = textBefore + (textBefore ? ' ' : '') + transcript;
    autoResize(input);
  };

  _recognition.onerror = (event) => {
    if (event.error === 'no-speech') {
      showToast('No speech detected — try again');
    } else if (event.error === 'not-allowed') {
      showToast('⚠ Microphone access denied. Check browser permissions.');
    } else {
      showToast('⚠ Voice error: ' + event.error);
    }
    stopVoice();
  };

  _recognition.onend = () => {
    stopVoice();
  };

  try {
    _recognition.start();
  } catch (e) {
    showToast('⚠ Could not start voice input');
    stopVoice();
  }
}

function stopVoice() {
  _isRecording = false;
  const micBtn = document.getElementById('mic-btn');
  if (micBtn) {
    micBtn.classList.remove('recording');
    micBtn.textContent = '🎤';
  }
  const chatInput = document.getElementById('chat-input');
  if (chatInput) chatInput.placeholder = 'Describe what you want to build...';

  if (_recognition) {
    try { _recognition.stop(); } catch (_) {}
    _recognition = null;
  }
}

// ── Drag-scroll for horizontal containers ──
export function initDragScroll(el) {
  if (!el) return;
  let isDown = false, startX, scrollLeft;
  el.addEventListener('mousedown', (e) => {
    isDown = true;
    startX = e.pageX - el.offsetLeft;
    scrollLeft = el.scrollLeft;
  });
  el.addEventListener('mouseleave', () => { isDown = false; });
  el.addEventListener('mouseup', () => { isDown = false; });
  el.addEventListener('mousemove', (e) => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    el.scrollLeft = scrollLeft - (x - startX) * 1.5;
  });
}

// ── Auto-scroll for gallery/carousel ──
export function initAutoScroll(el) {
  if (!el) return;
  let paused = false;
  let direction = 1;
  const speed = 0.5;

  el.addEventListener('mouseenter', () => { paused = true; });
  el.addEventListener('mouseleave', () => { paused = false; });
  el.addEventListener('touchstart', () => { paused = true; }, { passive: true });
  el.addEventListener('touchend', () => { setTimeout(() => { paused = false; }, 2000); });

  function scroll() {
    if (!paused && el.offsetParent !== null && el.scrollWidth > el.clientWidth + 2) {
      el.scrollLeft += speed * direction;
      if (el.scrollLeft >= el.scrollWidth - el.clientWidth - 1) direction = -1;
      else if (el.scrollLeft <= 0) direction = 1;
    }
    requestAnimationFrame(scroll);
  }

  requestAnimationFrame(scroll);
}
